import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { MdWarning } from 'react-icons/md';
import { Section, FieldGrid } from '../../components/EntityList';
import { formatDateString } from '../../components/FormattedDateTime/FormattedDateTime';
import { useLabels } from '../../hooks/useLabels';

function formatDate(dateValue) {
	return formatDateString({ dateValue: dateValue || '', dateFormat: 'DD/MM/YYYY' });
}

function CreditInfo({ credit }) {
	const { t } = useLabels();

	if (!credit) {
		return null;
	}

	const isLoan = Boolean(credit.concept && credit.concept.includes('prestamos'));
	const isAmortizable = credit.concept === 'prestamos-amortizable';

	const generationLeft = [
		{ label: t('form.creditStartDateLong'), value: formatDate(credit.date), required: true, mono: true },
		{ label: t('form.firstDueDayLong'), value: credit.dueDay, required: true },
		...(credit.firstDueExpiration
			? [{ label: t('msg.firstExpiration'), value: moment.utc(credit.firstDueExpiration).format('DD/MM/YYYY'), mono: true }]
			: []),
	];

	const generationRight = [
		{ label: t('form.generateFrom'), value: credit.selectedMonthOption, required: true },
		...(credit.selectedMonthOption === 'Otro'
			? [{ label: t('form.inHowManyMonths'), value: credit.numMonths, mono: true }]
			: []),
	];

	const accountingLeft = [
		{ label: t('entity.concept'), value: credit.concept },
		...(credit.concept === 'alquileres'
			? [
				{
					label: t('field.contract'),
					value: credit.contract ? (
						<Link to={`/AdminScreen/contracts/contract/${credit.contract._id || credit.contract}`}>
							{credit.contract.customId || t('msg.noContractAssigned')}
						</Link>
					) : null,
				},
			]
			: []),
		{
			label: t('entity.account.debtor'),
			value: credit.owner ? (
				<Link to={`/AdminScreen/accounts/account/${credit.owner._id || credit.owner}`}>
					{credit.owner.name || t('msg.noDebtorAssigned')}
				</Link>
			) : null,
		},
		{ label: t('field.currency'), value: credit.currency, mono: true },
		...(isLoan ? [{ label: t('field.periodicity'), value: credit.periodicity }] : []),
	];

	const accountingRight = [
		...(isLoan
			? [
				{ label: t('field.capital'), value: credit.capital, mono: true },
				{ label: t('form.interestPercent'), value: credit.interest, mono: true },
			]
			: []),
		...(isAmortizable ? [{ label: t('entity.due.plural'), value: credit.dues, mono: true }] : []),
		...(isLoan && !isAmortizable ? [{ label: t('form.endDate'), value: formatDate(credit.endDate), mono: true }] : []),
		{
			label: `${t('field.amount')}${isLoan ? ' cuota' : ''}`,
			value: credit.amount,
			mono: true,
		},
		{ label: t('field.totalGuaranteed'), value: credit.guaranteedAmount, mono: true },
	];

	const offSystemLeft = [
		{ label: t('form.hasPreviousCreditPlain'), value: credit.hasPreviousCredits ? 'Sí' : 'No' },
		{ label: t('field.previousCreditPlain'), value: credit.previousCredit, mono: true },
	];

	const offSystemRight = [
		{ label: t('form.previousCreditDatePlain'), value: formatDate(credit.previousCreditDate), mono: true },
	];

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<Section title={t('title.dueGenerationData')} description={t('msg.creditStartDateTooltip')}>
				<FieldGrid left={generationLeft} right={generationRight} />
			</Section>

			{credit.createWithPreviousDues && (
				<div className='tw:flex tw:items-start tw:gap-2 tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-ink-1'>
					<MdWarning className='tw:mt-0.5 tw:shrink-0 tw:text-warn' />
					<div>
						<div className='tw:font-medium'>
							{t('title.creditWithPreviousDuesPlain')} {credit.createWithPreviousPayments ? 'y pagas' : ''}
						</div>
						<div className='tw:text-ink-2'>{t('msg.creditCreatedWithPreviousDues')}</div>
					</div>
				</div>
			)}

			<Section title={t('title.accountingInfo')}>
				<FieldGrid left={accountingLeft} right={accountingRight} />
			</Section>

			<Section title={t('title.offSystemRecord')}>
				<FieldGrid left={offSystemLeft} right={offSystemRight} />
			</Section>
		</div>
	);
}

export default CreditInfo;
